import { createBrowserRouter, type RouteObject } from "react-router";
import React from "react";

import { type CheckoutSession } from "@/types";
import { useCheckoutRedirectHandler } from "@/modules/checkout-session";
import { PlanSelectionPage } from "@/modules/plan-selection/pages/plan-selection-page";
import { SuccessPage } from "@/modules/success/pages/success-page";
import { IVRPage } from "@/modules/ivr/pages/ivr-page";
import { ConfirmationPage } from "@/modules/payment-confirmation/pages/confirmation-page";
import EarlyRepaymentPage from "@/modules/early-repayment/pages/early-repayment-page";
import { ThreeDSRedirectPage } from "@/modules/three-ds/pages/three-ds-redirect-page";
import {
  QuestionnaireRedirectPage,
  QuestionnaireReturnPage,
} from "@/modules/questionnaire/pages/questionnaire-pages";
import { KycRedirectPage, KycReturnPage } from "@/modules/kyc/pages/kyc-pages";
import { HandshakePage } from "@/modules/handshake/pages/handshake-page";
import { detectAppBasename } from "@/lib/app-base";
import { NotFound } from "./not-found";
import { RouteError } from "./route-error";

function withCheckoutRedirect<
  T extends { onCheckoutFetched?: (session: CheckoutSession) => void },
>(Component: React.ComponentType<T>) {
  return function WrappedComponent(props: Omit<T, "onCheckoutFetched">) {
    const { handleCheckoutFetched } = useCheckoutRedirectHandler();
    return React.createElement(Component, {
      ...props,
      onCheckoutFetched: handleCheckoutFetched,
    } as T);
  };
}

const routes = [
  {
    path: "/",
    Component: PlanSelectionPage,
  },
  // Entry: checkout.tamara.co/handshake?checkoutId=… (no /v2 on this page).
  // Plan selection after the dwell still lives under the /v2 mount.
  {
    path: "/handshake",
    Component: HandshakePage,
  },
  {
    path: "/not-found",
    Component: NotFound,
  },
  {
    path: "/success",
    Component: withCheckoutRedirect(SuccessPage),
  },
  {
    path: "/ivr",
    Component: withCheckoutRedirect(IVRPage),
  },
  {
    path: "/confirmation",
    Component: withCheckoutRedirect(ConfirmationPage),
  },
  {
    path: "/early-repayment",
    Component: withCheckoutRedirect(EarlyRepaymentPage),
  },
  {
    path: "/instalment/add-payment-method/*",
    Component: withCheckoutRedirect(ConfirmationPage),
  },
  {
    path: "/questionnaire",
    Component: withCheckoutRedirect(QuestionnaireRedirectPage),
  },
  {
    path: "/questionnaire/return",
    Component: withCheckoutRedirect(QuestionnaireReturnPage),
  },
  {
    path: "/kyc",
    Component: withCheckoutRedirect(KycRedirectPage),
  },
  {
    path: "/kyc/return",
    Component: withCheckoutRedirect(KycReturnPage),
  },
  {
    path: "/3ds",
    Component: withCheckoutRedirect(ThreeDSRedirectPage),
  },
  {
    path: "/3ds/*",
    Component: withCheckoutRedirect(ConfirmationPage),
  },
] satisfies RouteObject[];

const basename = detectAppBasename();

// Every route gets the same boundary. Without one React Router renders its
// built-in developer error page (raw stack trace) straight at the customer,
// and the error never reaches New Relic.
export const router = createBrowserRouter(
  routes.map((route) => ({ ...route, ErrorBoundary: RouteError })),
  { basename },
);
